import { KNOWN_CLIENT_IDS } from '@/config/registry'

export { KNOWN_CLIENT_IDS }

function getHashQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash // e.g. '#/location?client=abc&key=349727'
  const qIndex = hash.indexOf('?')
  if (qIndex === -1) return null
  return new URLSearchParams(hash.slice(qIndex + 1)).get(name)
}

function isKnownClient(value: string | null): boolean {
  return value !== null && KNOWN_CLIENT_IDS.includes(value)
}

/**
 * Resolves the active client ID for this session.
 *
 * Precedence:
 *   1. ?client=<id>           — URL hash query param. Only honored when the
 *                                build sets VUE_APP_ALLOW_CLIENT_URL_OVERRIDE=true.
 *                                Used for demo/dev deploys where multi-client
 *                                switching is desired. Per-client production
 *                                builds leave this unset so end users can't
 *                                override the client off-band.
 *   2. VUE_APP_CLIENT_ID env  — set per build via package.json's build:<client>
 *                                script (cross-env VUE_APP_CLIENT_ID=<id>)
 *   3. 'accuweather'          — final fallback so the app always boots
 *
 * Any unknown value at any step is silently rejected and we move to the next step.
 *
 * Note: this used to gate on `NODE_ENV !== 'production'`, but that meant the
 * dev deploy (which runs `npm run build` = production mode) couldn't honor
 * the URL param. Setting NODE_ENV via .env.production broke webpack content
 * hashing as a side effect, so we moved to an explicit VUE_APP_ flag.
 */
export function resolveClientId(): string {
  if (process.env.VUE_APP_ALLOW_CLIENT_URL_OVERRIDE === 'true') {
    const queryClient = getHashQueryParam('client')?.toLowerCase() ?? null
    if (isKnownClient(queryClient)) return queryClient as string
  }
  const envClient = process.env.VUE_APP_CLIENT_ID ?? 'accuweather'
  return isKnownClient(envClient) ? envClient : 'accuweather'
}
